import { createFileRoute } from "@tanstack/react-router";
import { DashboardLayout } from "@/projects/components/dashboard-layout";
import { Card } from "@/projects/components/ui/card";
import { Button } from "@/projects/components/ui/button";
import { Download, Printer, FileBarChart } from "lucide-react";
import { useSubmissions, useTeachers } from "@/projects/lib/useStore";
import { StatusBadge } from "@/projects/components/status-badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/projects/components/ui/table";
import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/projects/components/ui/select";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/reports")({
  head: () => ({ meta: [{ title: "Reports — Admin" }] }),
  component: ReportsPage,
});

const reports = [
  { key: "date", label: "Date-wise Submission Report" },
  { key: "status", label: "Status-wise Submission Report" },
  { key: "teacher", label: "Teacher-wise Verification Report" },
  { key: "pending", label: "Pending Verification Report" },
  { key: "reward", label: "Ready for Reward Report" },
  { key: "notproper", label: "Not Proper Report" },
];

function ReportsPage() {
  const submissions = useSubmissions();
  const teachers = useTeachers();
  const [active, setActive] = useState(reports[0].key);
  const [teacherId, setTeacherId] = useState("all");

  const filtered = submissions.filter((s) => {
    if (active === "pending" && s.status !== "Assigned for Verification") return false;
    if (active === "reward" && s.status !== "Ready for Reward") return false;
    if (active === "notproper" && s.status !== "Not Proper") return false;
    if (teacherId !== "all" && s.assignedTeacherId !== teacherId) return false;
    return true;
  });

  return (
    <DashboardLayout role="admin" title="Reports" breadcrumb={["Home", "Reports"]}>
      <div className="grid gap-4 lg:grid-cols-4">
        <Card className="p-3 lg:col-span-1">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Available Reports</div>
          <div className="mt-2 space-y-1">
            {reports.map((r) => (
              <button
                key={r.key}
                onClick={() => setActive(r.key)}
                className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm ${active === r.key ? "bg-royal-soft text-royal" : "hover:bg-muted"}`}
              >
                <FileBarChart className="h-4 w-4" />
                {r.label}
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-4 lg:col-span-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-lg font-semibold">{reports.find((r) => r.key === active)?.label}</div>
              <div className="text-xs text-muted-foreground">{filtered.length} records</div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={teacherId} onValueChange={setTeacherId}>
                <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teachers</SelectItem>
                  {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => { window.print(); }}>
                <Printer className="mr-2 h-4 w-4" /> Print
              </Button>
              <Button size="sm" onClick={() => toast.success("Excel exported successfully")}>
                <Download className="mr-2 h-4 w-4" /> Export
              </Button>
            </div>
          </div>

          <div className="mt-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Teacher</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">No records found.</TableCell></TableRow>
                )}
                {filtered.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono text-xs text-royal">{s.id}</TableCell>
                    <TableCell>{s.studentName}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{s.videoTitle}</TableCell>
                    <TableCell>{s.assignedTeacherName ?? "—"}</TableCell>
                    <TableCell><StatusBadge status={s.status} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(s.submittedAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
